import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Eye, EyeOff, CreditCard as Edit, Trash2, BarChart3, Zap } from 'lucide-react';
import { useProMaxModels, toggleProMaxModelAvailability, deleteProMaxModel } from '../hooks/useProMaxModels';
import { parseAdminStaffSession } from '../utils/adminSession';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AdminPageToolbar } from '../components/admin/AdminLayout';

type FilterType = 'all' | 'active' | 'inactive' | 'featured';
type CategoryFilter = 'all' | 'image' | 'video' | 'audio' | 'text';

export const ProMaxModelsPage = () => {
  const navigate = useNavigate();
  const { models, loading, error, refetch } = useProMaxModels(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [updatingModels, setUpdatingModels] = useState<Set<string>>(new Set());
  const [deletingModel, setDeletingModel] = useState<string | null>(null);

  useEffect(() => {
    const access = parseAdminStaffSession();
    if (!access) {
      navigate('/admin');
      return;
    }
    if (access?.role !== 'superadmin') {
      alert('Access denied. Only superadmins can manage ProMax models.');
      navigate('/admin');
    }
  }, [navigate]);

  const handleToggleAvailability = async (modelId: string, currentStatus: boolean) => {
    setUpdatingModels(prev => new Set([...prev, modelId]));

    const result = await toggleProMaxModelAvailability(modelId, currentStatus);

    if (result.success) {
      await refetch();
    } else {
      alert(`Failed to update model status: ${result.error}`);
    }

    setUpdatingModels(prev => {
      const newSet = new Set(prev);
      newSet.delete(modelId);
      return newSet;
    });
  };

  const handleEditModel = (modelId: string) => {
    navigate(`/admin/promax-models/edit/${modelId}`);
  };

  const handleCreateNew = () => {
    navigate('/admin/promax-models/create');
  };

  const handleDeleteModel = async (modelId: string) => {
    if (!confirm('Are you sure you want to delete this ProMax model? This action cannot be undone.')) {
      return;
    }

    setDeletingModel(modelId);

    const result = await deleteProMaxModel(modelId);

    if (result.success) {
      await refetch();
      alert('Model deleted successfully');
    } else {
      alert(`Failed to delete model: ${result.error}`);
    }

    setDeletingModel(null);
  };

  const handleViewAnalytics = (modelId: string) => {
    navigate(`/admin/promax-models/analytics?model=${modelId}`);
  };

  const filteredModels = models.filter(model => {
    const matchesSearch = model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         model.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         model.model_id.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = filter === 'all' ||
                         (filter === 'active' && model.is_active) ||
                         (filter === 'inactive' && !model.is_active) ||
                         (filter === 'featured' && model.is_featured);

    const matchesCategory = categoryFilter === 'all' || model.category === categoryFilter;

    return matchesSearch && matchesFilter && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-status-error mb-4">{error}</p>
          <button onClick={refetch} className="button-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-base">
      <AdminPageToolbar
        maxWidthClass="max-w-7xl"
        title="ProMax Models"
        subtitle="Manage custom FAL model configurations"
      >
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/admin/promax-models/analytics')}
            className="button-secondary flex items-center gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            Analytics
          </button>
          <button type="button" onClick={handleCreateNew} className="button-primary flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Create Model
          </button>
        </div>
      </AdminPageToolbar>

      <div className="min-h-0 flex-1 overflow-y-auto max-w-7xl mx-auto w-full px-4 sm:px-6 pb-8">
        <div className="bg-elevated border border-subtle rounded-lg p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-th-secondary" />
                <input
                  type="text"
                  placeholder="Search models..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-10 w-full"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as FilterType)}
                className="input-field"
              >
                <option value="all">All Models</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="featured">Featured</option>
              </select>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as CategoryFilter)}
                className="input-field"
              >
                <option value="all">All Categories</option>
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
                <option value="text">Text</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-elevated border border-subtle rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-elevated border-b border-subtle">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-th-secondary uppercase tracking-wider">
                    Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-th-secondary uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-th-secondary uppercase tracking-wider">
                    Credits
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-th-secondary uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-th-secondary uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-solidBorder">
                {filteredModels.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-th-secondary">
                      {searchTerm || filter !== 'all' || categoryFilter !== 'all'
                        ? 'No models match your filters'
                        : 'No ProMax models yet. Create your first model!'}
                    </td>
                  </tr>
                ) : (
                  filteredModels.map((model) => (
                    <tr key={model.id} className="hover:bg-elevated transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 ${model.icon_bg} rounded-lg`}>
                            <Zap className={`w-5 h-5 ${model.icon_color}`} />
                          </div>
                          <div>
                            <div className="font-medium text-th-primary flex items-center gap-2">
                              {model.name}
                              {model.is_featured && (
                                <span className="px-2 py-0.5 bg-status-warning/20 text-status-warning text-xs rounded">
                                  Featured
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-th-secondary">{model.model_id}</div>
                            <div className="text-xs text-th-secondary mt-1 max-w-md truncate">
                              {model.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-brand/20 text-brand text-xs rounded capitalize">
                          {model.category}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-th-primary font-medium">{model.credit_cost}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            model.is_active
                              ? 'bg-status-success/20 text-status-success'
                              : 'bg-status-error/20 text-status-error'
                          }`}
                        >
                          {model.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleAvailability(model.model_id, model.is_active)}
                            disabled={updatingModels.has(model.model_id)}
                            className="p-2 hover:bg-overlay rounded transition-colors disabled:opacity-50"
                            title={model.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {model.is_active ? (
                              <EyeOff className="w-4 h-4 text-th-secondary" />
                            ) : (
                              <Eye className="w-4 h-4 text-th-secondary" />
                            )}
                          </button>
                          <button
                            onClick={() => handleEditModel(model.model_id)}
                            className="p-2 hover:bg-overlay rounded transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4 text-th-secondary" />
                          </button>
                          <button
                            onClick={() => handleViewAnalytics(model.model_id)}
                            className="p-2 hover:bg-overlay rounded transition-colors"
                            title="View Analytics"
                          >
                            <BarChart3 className="w-4 h-4 text-th-secondary" />
                          </button>
                          <button
                            onClick={() => handleDeleteModel(model.model_id)}
                            disabled={deletingModel === model.model_id}
                            className="p-2 hover:bg-overlay rounded transition-colors text-status-error disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 text-center text-th-secondary text-sm">
          Showing {filteredModels.length} of {models.length} models
        </div>
      </div>
    </div>
  );
};
